/**
 * The agent's presence: ONE orb, and the only ambient motion in the interface.
 *
 * A bright core inside a soft halo. It is deliberately NOT the brand green — the
 * agent is not the wordmark. The gradient runs white → icy blue → blue-green, from
 * tokens in `src/app/agent/theme.css`, so nothing here hardcodes a colour.
 *
 * The three states carry real meaning, and the caller derives them from the live
 * run rather than from anything the model claims:
 *   working     — slow 2.4s breathing pulse on the halo, unmistakable at a glance;
 *                 generation is actually happening right now
 *   waiting     — steady soft glow; the agent is present and holding
 *   needs-input — the same orb, steadily brighter; a human decision is open
 *
 * No flashing, no rapid blinking, no second animation, and `prefers-reduced-motion`
 * falls back to a steady glow (theme.css).
 */
import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { cn } from '@/app/lib/utils';

export type AgentStatus = 'working' | 'waiting' | 'needs-input';

const messages = defineMessages({
  working: {
    id: 'statusLight.working',
    defaultMessage: 'The agent is working',
    description: 'Accessible label for the agent status light while it is generating',
  },
  waiting: {
    id: 'statusLight.waiting',
    defaultMessage: 'The agent is ready',
    description: 'Accessible label for the agent status light while it is idle',
  },
  needsInput: {
    id: 'statusLight.needsInput',
    defaultMessage: 'The agent needs your decision',
    description: 'Accessible label for the agent status light while it awaits a human answer',
  },
});

const GRADIENT =
  'radial-gradient(circle at 34% 30%, hsl(var(--signal-core)) 0%, hsl(var(--signal-core)) 22%, hsl(var(--signal-mid)) 58%, hsl(var(--signal-edge)) 100%)';

const STATE_CLASS: Record<AgentStatus, string> = {
  working: 'signal-breathe',
  waiting: 'signal-steady',
  'needs-input': 'signal-attention',
};

export const StatusLight: FC<{ status: AgentStatus; className?: string }> = ({
  status,
  className,
}) => {
  const intl = useIntl();
  const label =
    status === 'working'
      ? messages.working
      : status === 'needs-input'
        ? messages.needsInput
        : messages.waiting;

  return (
    <span
      /* The box reserves room for the halo so the glow is never clipped by the
         panel's layout. */
      className={cn('inline-flex h-7 w-7 flex-none items-center justify-center', className)}
      role="status"
      aria-label={intl.formatMessage(label)}
      data-agent-status={status}
    >
      <span
        aria-hidden="true"
        className={cn('h-3 w-3 rounded-full', STATE_CLASS[status])}
        style={{ backgroundImage: GRADIENT }}
      />
    </span>
  );
};
